/**
 * GuideViewerPopover Component
 *
 * Displays complete guide content in a compact popover (400×300px).
 *
 * Features:
 * - Fixed 400×300px size with scrollable content
 * - Header with title and close button (top-right)
 * - All content items with scaled images/videos
 * - Theme-integrated styling
 */

import React, { useState, useEffect, useCallback } from 'react';
import {
  Popover,
  Popper,
  Paper,
  Box,
  Typography,
  IconButton,
  CircularProgress,
  Divider,
  Snackbar,
  Alert,
  Tooltip,
  useTheme
} from '@mui/material';
import { Close as CloseIcon, ContentCopy as ContentCopyIcon } from '@mui/icons-material';
import type { GuideService } from '@modules/firebase-core/services/GuideService';
import type { Guide } from '@segmently/schema-models/auth';
import { GuideContentItem } from './GuideContentItem';
import { useSupportLocation } from '../support-location/SupportLocationContext';
import { buildSupportLocationText } from '../support-location/buildSupportLocationText';

export interface GuideViewerPopoverProps {
  /** Whether the popover is open */
  open: boolean;

  /** Anchor element for positioning */
  anchorEl: HTMLElement | null;

  /** Callback when popover should close */
  onClose: () => void;

  /** Guide key to fetch and display */
  guideKey: string;

  /** Guide service instance for fetching guide data */
  guideService: GuideService;

  /** Opened via hover (not click): don't steal focus and let the underlying UI stay interactive. */
  hoverMode?: boolean;

  /** Keep the popover open while the pointer is over it. */
  onMouseEnter?: () => void;
  onMouseLeave?: () => void;
}

/**
 * Session cache so repeated hovers over the same anchor don't re-show the loader.
 * `invalidateGuideCache` lets the guide editor bust an entry after a save.
 */
const guideCache = new Map<string, Guide | null>();
const inFlightGuide = new Map<string, Promise<Guide | null>>();

export function invalidateGuideCache(key?: string): void {
  if (key) {
    guideCache.delete(key);
  } else {
    guideCache.clear();
  }
}

async function fetchGuideCached(guideService: GuideService, guideKey: string): Promise<Guide | null> {
  if (guideCache.has(guideKey)) return guideCache.get(guideKey) ?? null;
  const existing = inFlightGuide.get(guideKey);
  if (existing) return existing;
  const promise = guideService
    .query('key', '==', guideKey)
    .then(guides => {
      const guide = guides.length === 0 ? null : guides[0];
      guideCache.set(guideKey, guide);
      inFlightGuide.delete(guideKey);
      return guide;
    })
    .catch(err => {
      inFlightGuide.delete(guideKey);
      throw err;
    });
  inFlightGuide.set(guideKey, promise);
  return promise;
}

export const GuideViewerPopover: React.FC<GuideViewerPopoverProps> = ({
  open,
  anchorEl,
  onClose,
  guideKey,
  guideService,
  hoverMode = false,
  onMouseEnter,
  onMouseLeave,
}) => {
  const theme = useTheme();
  const [guide, setGuide] = useState<Guide | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [copied, setCopied] = useState<boolean>(false);

  // In-editor screen context (empty {} when shown outside the screen editor → URL-only address).
  const supportLocation = useSupportLocation();

  const handleCopyLocation = useCallback(async () => {
    const text = buildSupportLocationText({
      guideKey,
      guideName: guide?.name,
      snapshot: supportLocation,
      href: typeof window !== 'undefined' ? window.location.href : undefined,
      pathname: typeof window !== 'undefined' ? window.location.pathname : undefined,
      builderApiUrl: import.meta.env?.VITE_BUILDER_API_URL as string | undefined,
    });
    try {
      await navigator.clipboard?.writeText(text);
      setCopied(true);
    } catch (err) {
      console.error('[GuideViewerPopover] Failed to copy support location:', err);
    }
  }, [guideKey, guide?.name, supportLocation]);

  // Fetch guide when popover opens
  const fetchGuide = useCallback(async () => {
    if (!open || !guideKey) return;

    // Warm cache → render instantly without a spinner (no flicker on repeat hover).
    if (guideCache.has(guideKey)) {
      setGuide(guideCache.get(guideKey) ?? null);
      setError(null);
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      setGuide(await fetchGuideCached(guideService, guideKey));
    } catch (err: any) {
      console.error('[GuideViewerPopover] Failed to fetch guide:', err);
      setError(err?.message || 'Failed to load guide');
      setGuide(null);
    } finally {
      setLoading(false);
    }
  }, [open, guideKey, guideService]);

  useEffect(() => {
    fetchGuide();
  }, [fetchGuide]);

  // Reset state when popover closes
  useEffect(() => {
    if (!open) {
      setGuide(null);
      setError(null);
      setLoading(false);
    }
  }, [open]);

  const paperSx = {
    width: 400,
    height: 300,
    display: 'flex',
    flexDirection: 'column' as const,
    overflow: 'hidden',
    backgroundColor: theme.palette.background.default,
    backgroundImage: 'none',
  };

  const body = (
    <>
      {/* Header */}
      <Box
        sx={{
          p: 2,
          borderBottom: `1px solid ${theme.palette.divider}`,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          minHeight: 56,
        }}
      >
        <Typography
          variant="h6"
          sx={{ flex: 1, pr: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
        >
          {guide?.name || 'Guide'}
        </Typography>
        <Tooltip title="Copy my location for support">
          <IconButton
            onClick={handleCopyLocation}
            size="small"
            data-testid="guide-copy-location"
            aria-label="Copy my location for support"
            sx={{ color: theme.palette.text.secondary, '&:hover': { color: theme.palette.text.primary } }}
          >
            <ContentCopyIcon fontSize="small" />
          </IconButton>
        </Tooltip>
        <IconButton
          onClick={onClose}
          size="small"
          aria-label="Close guide"
          sx={{ color: theme.palette.text.secondary, '&:hover': { color: theme.palette.text.primary } }}
        >
          <CloseIcon fontSize="small" />
        </IconButton>
      </Box>

      {/* Content Area */}
      <Box sx={{ flex: 1, overflow: 'auto', p: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
        {loading && (
          <Box display="flex" justifyContent="center" alignItems="center" flex={1}>
            <CircularProgress size={32} />
          </Box>
        )}

        {error && !loading && (
          <Box textAlign="center">
            <Typography variant="body2" color="error" gutterBottom>Error Loading Guide</Typography>
            <Typography variant="caption" color="text.secondary">{error}</Typography>
          </Box>
        )}

        {!loading && !error && !guide && (
          <Box textAlign="center">
            <Typography variant="body2" color="text.secondary">No guide found with key "{guideKey}"</Typography>
          </Box>
        )}

        {!loading && !error && guide && (!guide.content || guide.content.length === 0) && (
          <Box textAlign="center">
            <Typography variant="body2" color="text.secondary">This guide has no content yet.</Typography>
          </Box>
        )}

        {!loading && !error && guide && guide.content && guide.content.length > 0 && (
          <Box sx={{ '& img': { maxHeight: 150 }, '& video': { maxHeight: 150 } }}>
            {guide.content.map((item, index) => (
              <React.Fragment key={index}>
                <GuideContentItem
                  title={item.title}
                  subtitle={item.subtitle}
                  description={item.description}
                  image={item.image}
                  video={item.video}
                  fullArticleLink={item.fullArticleLink}
                />
                {index < guide.content!.length - 1 && (
                  <Divider sx={{ my: 2, borderColor: theme.palette.divider }} />
                )}
              </React.Fragment>
            ))}
          </Box>
        )}
      </Box>

      <Snackbar
        open={copied}
        autoHideDuration={2000}
        onClose={() => setCopied(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity="success" variant="filled" onClose={() => setCopied(false)} sx={{ width: '100%' }}>
          Location copied — paste it to support
        </Alert>
      </Snackbar>
    </>
  );

  // Hover mode: a pure Popper — NO modal, backdrop, scroll-lock or focus trap — so opening
  // it cannot steal the hover from its own trigger (the cause of the open/close flicker).
  if (hoverMode) {
    return (
      <Popper
        open={open}
        anchorEl={anchorEl}
        placement="bottom-end"
        style={{ zIndex: theme.zIndex.tooltip }}
        modifiers={[
          { name: 'offset', options: { offset: [0, 6] } },
          { name: 'preventOverflow', options: { padding: 8 } },
        ]}
      >
        <Paper
          data-testid={`guide-popover-${guideKey}`}
          elevation={8}
          onMouseEnter={onMouseEnter}
          onMouseLeave={onMouseLeave}
          sx={paperSx}
        >
          {body}
        </Paper>
      </Popper>
    );
  }

  // Click (pinned) mode: a real modal Popover with backdrop, click-away and Escape.
  return (
    <Popover
      data-testid={`guide-popover-${guideKey}`}
      open={open}
      anchorEl={anchorEl}
      onClose={onClose}
      disableScrollLock
      anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      transformOrigin={{ vertical: 'top', horizontal: 'right' }}
      slotProps={{ paper: { sx: paperSx } }}
    >
      {body}
    </Popover>
  );
};

GuideViewerPopover.displayName = 'GuideViewerPopover';
